import { useState, useEffect, ReactNode } from "react";
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    InputLabel,
    TextField,
    Typography,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import * as yup from "yup";
import { useFormik } from "formik";
import { useSearchParams } from "react-router-dom";
import useAuth from "src/hooks/useAuth";
import useMessage from "src/hooks/useMessage";
import api from "src/utils/apiConfig";
import PasswordField from "src/components/input/PasswordTextField";
import SpinnerLoader from "src/components/loader/SpinnerLoader";
import {
    formLabelStyle,
    formBorderRadius,
    formItemHeight,
    formItemFontSize,
} from "src/forms/Form.styles";
import autoBackorderLogo from "src/assets/images/headerHorizontalLogo.webp";

const btnStyle = {
    borderRadius: formBorderRadius,
    height: formItemHeight,
    textTransform: "none",
    fontSize: formItemFontSize,
    fontWeight: 600,
};

interface GroupedScopes {
    group: string;
    groupLabel: string;
    scopes: { scope: string; description: string }[];
}

interface ConsentData {
    clientName: string;
    requestedScopes: string[];
    groupedScopes: GroupedScopes[];
}

function ConsentCard({
    children,
    wide = false,
}: {
    children: ReactNode;
    wide?: boolean;
}) {
    return (
        <Box sx={pageWrap}>
            <Box sx={{ ...card, maxWidth: wide ? { xs: 480, md: 860 } : 480 }}>
                <Box sx={brandHeader}>
                    <img
                        src={autoBackorderLogo}
                        alt="AutoBackorder"
                        height={40}
                    />
                </Box>
                <Box sx={cardBody}>{children}</Box>
            </Box>
        </Box>
    );
}

export default function OAuthConsentPage() {
    const auth = useAuth();
    const { showError } = useMessage();
    const [searchParams] = useSearchParams();

    const sessionKey = searchParams.get("s") ?? "";

    const [loginLoading, setLoginLoading] = useState(false);
    const [consentLoading, setConsentLoading] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [consentData, setConsentData] = useState<ConsentData | null>(null);
    const [consentFetching, setConsentFetching] = useState(false);
    const [sessionExpired, setSessionExpired] = useState(false);

    useEffect(() => {
        setIsLoggedIn(!!(auth.isLogged && auth.user));
    }, [auth.isLogged, auth.user]);

    useEffect(() => {
        if (!sessionKey) return;
        setConsentFetching(true);
        api.get(`/oauth/consent-data?s=${sessionKey}`)
            .then((res) => setConsentData(res.data))
            .catch((err) => {
                if (err?.response?.status === 404) {
                    setSessionExpired(true);
                } else {
                    showError("Failed to load authorization request.");
                }
            })
            .finally(() => setConsentFetching(false));
    }, [sessionKey]);

    // ── Login form ───────────────────────────────────────────────────────

    const loginSchema = yup.object({
        username: yup.string().required("Username is required"),
        password: yup.string().required("Password is required"),
    });

    const loginFormik = useFormik({
        initialValues: { username: "", password: "" },
        validationSchema: loginSchema,
        async onSubmit(values) {
            setLoginLoading(true);
            try {
                await auth.login(values.username, values.password, false);
            } catch (error: any) {
                showError(
                    error?.response?.data?.message ||
                        "Login failed. Please check your credentials.",
                );
            } finally {
                setLoginLoading(false);
            }
        },
    });

    // ── Consent submit ───────────────────────────────────────────────────

    async function submitConsent(approved: boolean) {
        setConsentLoading(true);
        try {
            const response = await api.post("/oauth/authorize", {
                sessionKey,
                approved,
            });
            const { redirectUrl } = response.data;
            if (redirectUrl) {
                window.location.href = redirectUrl;
            }
        } catch (error: any) {
            showError("Something went wrong. Please try again.");
        } finally {
            setConsentLoading(false);
        }
    }

    // ── Guard: no session key ────────────────────────────────────────────

    if (!sessionKey) {
        return (
            <ConsentCard>
                <Typography sx={titleStyle} color="error">
                    Invalid Request
                </Typography>
                <Typography sx={bodyText}>
                    Required OAuth parameters are missing. Please initiate the
                    flow from the application.
                </Typography>
            </ConsentCard>
        );
    }

    // ── Guard: session expired ───────────────────────────────────────────

    if (sessionExpired) {
        return (
            <ConsentCard>
                <Typography sx={titleStyle} color="error">
                    Session Expired
                </Typography>
                <Typography sx={bodyText}>
                    This authorization request has expired. Please return to the
                    application and try again.
                </Typography>
            </ConsentCard>
        );
    }

    // ── Loading consent data ─────────────────────────────────────────────

    if (consentFetching || !consentData) {
        return (
            <ConsentCard>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        py: 4,
                    }}
                >
                    <CircularProgress size={32} sx={{ color: "#EFD016" }} />
                </Box>
            </ConsentCard>
        );
    }

    return (
        <ConsentCard wide>
            <Box sx={twoCol}>
                {/* Left: identity + requested permissions */}
                <Box sx={leftCol}>
                    <Typography sx={titleStyle}>
                        Authorization Request
                    </Typography>
                    <Typography sx={bodyText}>
                        <strong>{consentData.clientName}</strong> is requesting
                        access to your AutoBackorder account.
                    </Typography>

                    <Typography
                        sx={{ ...bodyText, fontWeight: 600, mt: 3, mb: 1 }}
                    >
                        This application will be able to:
                    </Typography>
                    <Box sx={scopeList}>
                        {consentData.groupedScopes
                            .flatMap((g) => g.scopes)
                            .map(({ scope, description }) => (
                                <Box key={scope} sx={scopeRow}>
                                    <CheckRoundedIcon sx={checkIcon} />
                                    <Typography sx={scopeText}>
                                        {description}
                                    </Typography>
                                </Box>
                            ))}
                    </Box>
                </Box>

                {/* Right: login / consent actions */}
                <Box sx={rightCol}>
                    {!isLoggedIn ? (
                        <form
                            onSubmit={loginFormik.handleSubmit}
                            autoComplete="off"
                            style={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <InputLabel htmlFor="username" sx={darkLabel}>
                                Username
                            </InputLabel>
                            <TextField
                                fullWidth
                                id="username"
                                name="username"
                                autoComplete="off"
                                value={loginFormik.values.username}
                                sx={popupFieldStyle}
                                onChange={(e) =>
                                    loginFormik.setFieldValue(
                                        "username",
                                        e.target.value.trim(),
                                    )
                                }
                                error={
                                    loginFormik.touched.username &&
                                    !!loginFormik.errors.username
                                }
                                helperText={
                                    loginFormik.touched.username &&
                                    loginFormik.errors.username
                                }
                            />
                            <InputLabel htmlFor="password" sx={darkLabel}>
                                Password
                            </InputLabel>
                            <PasswordField
                                form={loginFormik}
                                sx={popupFieldStyle}
                                formValueName="password"
                            />
                            <Button
                                type="submit"
                                fullWidth
                                disabled={loginLoading}
                                sx={{
                                    ...btnStyle,
                                    mt: 1.5,
                                    bgcolor: "#EFD016",
                                    color: "#000",
                                    "&:hover": { bgcolor: "#d4b800" },
                                }}
                                endIcon={
                                    <SpinnerLoader
                                        loading={loginLoading}
                                        size={18}
                                        sx={{ ml: 1 }}
                                    />
                                }
                            >
                                Sign In &amp; Continue
                            </Button>
                            <Typography sx={noAccountText}>
                                Don&apos;t have an AutoBackorder account?{" "}
                                <Box
                                    component="a"
                                    href="/register"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={registerLink}
                                >
                                    Create one
                                </Box>
                            </Typography>
                        </form>
                    ) : (
                        <Box sx={signedInWrap}>
                            <Avatar sx={avatarStyle}>
                                {auth.user?.username?.[0]?.toUpperCase() ?? "?"}
                            </Avatar>
                            <Typography
                                sx={{ ...bodyText, color: "#888", mb: 0.25 }}
                            >
                                Signed in as
                            </Typography>
                            <Typography sx={signedInName}>
                                {auth.user?.username}
                            </Typography>
                            <Typography sx={signedInPrompt}>
                                Allow <strong>{consentData.clientName}</strong>{" "}
                                to access your account?
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1.5,
                                    width: "100%",
                                }}
                            >
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    disabled={consentLoading}
                                    onClick={() => submitConsent(false)}
                                    sx={{
                                        ...btnStyle,
                                        borderColor: "rgba(255,255,255,0.3)",
                                        color: "#e8eaed",
                                        "&:hover": {
                                            borderColor:
                                                "rgba(255,255,255,0.5)",
                                            bgcolor: "rgba(255,255,255,0.04)",
                                        },
                                    }}
                                >
                                    Deny
                                </Button>
                                <Button
                                    fullWidth
                                    disabled={consentLoading}
                                    onClick={() => submitConsent(true)}
                                    sx={{
                                        ...btnStyle,
                                        bgcolor: "#EFD016",
                                        color: "#000",
                                        "&:hover": { bgcolor: "#d4b800" },
                                    }}
                                    endIcon={
                                        consentLoading ? (
                                            <CircularProgress
                                                size={16}
                                                sx={{ ml: 1 }}
                                            />
                                        ) : null
                                    }
                                >
                                    Allow Access
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>

            <Typography
                sx={{
                    ...bodyText,
                    mt: 3,
                    color: "#999",
                    fontSize: "12px",
                    textAlign: "center",
                }}
            >
                You can revoke access for this app at any time from your
                AutoBackorder account
            </Typography>
        </ConsentCard>
    );
}

// ── Popup-optimized styles ────────────────────────────────────────────────────

const pageWrap = {
    minHeight: "100vh",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bgcolor: "#202124",
    p: 2,
};

const card = {
    borderRadius: "18px",
    maxWidth: 480,
    width: "100%",
    boxShadow: "0 24px 60px rgba(0,0,0,0.55), 0 8px 20px rgba(0,0,0,0.35)",
};

const brandHeader = {
    display: "flex",
    alignItems: "center",
    px: { xs: 3, sm: 4 },
    py: 2,
    background: "linear-gradient(110deg, #EFD016 74%, #131314 74%)",
    borderTopLeftRadius: "18px",
    borderTopRightRadius: "18px",
};

const cardBody = {
    bgcolor: "#131314",
    p: { xs: 2.5, sm: 4 },
    borderBottomLeftRadius: "18px",
    borderBottomRightRadius: "18px",
};

const twoCol = {
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    alignItems: { md: "stretch" },
    gap: { xs: 2.5, md: 5 },
};

const leftCol = {
    flex: 1,
    minWidth: 0,
};

const rightCol = {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    borderLeft: { md: "1px solid rgba(255,255,255,0.1)" },
    borderTop: { xs: "1px solid rgba(255,255,255,0.1)", md: "none" },
    pl: { md: 4 },
    pt: { xs: 2.5, md: 0 },
};

const signedInWrap = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    my: "auto", // keep the short confirmation block vertically centered
};

const avatarStyle = {
    bgcolor: "#EFD016",
    color: "#000",
    width: 56,
    height: 56,
    fontSize: "22px",
    fontWeight: 700,
    mb: 1.5,
};

const signedInName = {
    fontWeight: 700,
    fontSize: "16px",
    color: "#e8eaed",
};

const signedInPrompt = {
    fontSize: "13px",
    color: "#9aa0a6",
    lineHeight: 1.5,
    mt: 1,
    mb: 2.5,
};

const scopeList = {
    display: "flex",
    flexDirection: "column",
    gap: 1.25,
};

const scopeRow = {
    display: "flex",
    alignItems: "flex-start",
    gap: 1,
};

const checkIcon = {
    color: "#1ba94c",
    fontSize: "18px",
    mt: "1px",
    flexShrink: 0,
};

const scopeText = {
    fontSize: "13px",
    color: "#bdc1c6",
    lineHeight: 1.5,
};

const titleStyle = {
    fontSize: { xs: "20px", sm: "24px" },
    fontWeight: 700,
    mb: 1,
    color: "#e8eaed",
};

const bodyText = {
    fontSize: "14px",
    color: "#bdc1c6",
    lineHeight: 1.6,
};

const darkLabel = {
    ...formLabelStyle,
    color: "#e8eaed",
    fontSize: { xs: "12px", md: "13px" },
    marginBottom: "6px",
};

// Compact field style for the popup: smaller height and a tight gap between the
// two inputs so Username/Password read as one group, with the button set apart.
const popupFieldStyle = {
    " & .MuiOutlinedInput-root": {
        height: "38px",
        borderRadius: "12px",
        backgroundColor: "white",
        marginBottom: "22px",
    },
    " & .MuiOutlinedInput-input": {
        fontSize: "13px",
    },
    " & .MuiFormHelperText-root": {
        marginX: 0,
        marginTop: "3px",
        marginBottom: "3px",
    },
    " & .MuiOutlinedInput-input:-webkit-autofill": {
        height: "0px",
        marginX: "7px",
    },
};

const noAccountText = {
    mt: "auto",
    pt: 2,
    fontSize: "13px",
    color: "#9aa0a6",
    textAlign: "center",
};

const registerLink = {
    color: "#EFD016",
    fontWeight: 600,
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
};
